import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ShoppingBag, 
  Truck, 
  CreditCard, 
  ChevronRight, 
  Trash2, 
  Plus, 
  Minus,
  User,
  Building2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { isDiscountActive, getDiscountPercentage } from '../config/discountConfig';
import { isCutleryProduct, calculateTotalUnits, getUnitsPerPack } from '../config/productConfig';
import '../styles/pages.css';

const Shop = () => {
  const navigate = useNavigate();
  const { 
    cartItems, 
    removeFromCart, 
    updateQuantity, 
    getCartTotal,
    getCartOriginalTotal,
    getCartSavings,
    getItemPrice,
    getItemPriceInfo,
    getItemDetails,
    canIncreaseCartons,
    canDecreaseCartons,
    hasActiveDiscount,
    clearCart 
  } = useCart();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    zipCode: '',
    streetName: '',
    houseNumber: '',
    customerType: 'private',
    companyName: '',
    city: '',
    country: 'Netherlands'
  });

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [createAccount, setCreateAccount] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [accountCreationStatus, setAccountCreationStatus] = useState(null); // 'success', 'error', or null

  // Price calculations
  const SHIPPING_COST_BASE = 7.49;
  const FREE_SHIPPING_THRESHOLD = 50;
  const VAT_RATE = 0.09; // 9%

  const netTotal = getCartTotal(); // This now returns discounted total
  const originalNetTotal = getCartOriginalTotal ? getCartOriginalTotal() : netTotal;
  const discountActive = hasActiveDiscount ? hasActiveDiscount() : false;
  const cartSavings = getCartSavings ? getCartSavings() : 0;
  const isFreeShipping = netTotal >= FREE_SHIPPING_THRESHOLD;
  const shippingCost = isFreeShipping ? 0 : SHIPPING_COST_BASE;
  const vatAmount = (netTotal + shippingCost) * VAT_RATE;
  const finalTotal = netTotal + shippingCost + vatAmount;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.mobile.trim()) newErrors.mobile = 'Mobile number is required';
    if (formData.customerType === 'business' && !formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required for business customers';
    }
    if (!formData.zipCode.trim()) newErrors.zipCode = 'Zip/Postal code is required';
    if (!formData.streetName.trim()) newErrors.streetName = 'Street name is required';
    if (!formData.houseNumber.trim()) newErrors.houseNumber = 'House number is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!acceptedTerms) newErrors.terms = 'You must accept the terms and conditions';
    
    // Password validation only if creating account
    if (createAccount) {
      if (!password) {
        newErrors.password = 'Password is required to create an account';
      } else if (password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (cartItems.length === 0) {
      alert('Your cart is empty. Please add items before checkout.');
      return;
    }

    setLoading(true);
    setAccountCreationStatus(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // If user wants to create account, do it first (but don't block checkout on failure)
      if (createAccount && password) {
        try {
          const registerResponse = await fetch(`${backendUrl}/api/users/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email: formData.email,
              password: password,
              firstName: formData.firstName,
              lastName: formData.lastName,
              mobile: formData.mobile,
              zipCode: formData.zipCode,
              streetName: formData.streetName,
              houseNumber: formData.houseNumber,
              city: formData.city,
              country: formData.country,
              customerType: formData.customerType,
              companyName: formData.companyName
            })
          });
          
          if (registerResponse.ok) {
            setAccountCreationStatus('success');
            console.log('Account created successfully');
          } else {
            const errorData = await registerResponse.json();
            setAccountCreationStatus('error');
            console.log('Account creation failed:', errorData.detail);
            // Don't block checkout - just log the error
          }
        } catch (regError) {
          setAccountCreationStatus('error');
          console.log('Account creation error:', regError);
          // Don't block checkout - continue with order
        }
      }
      
      const orderData = {
        customer: formData,
        items: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          variant: item.variant,
          price: item.price,
          quantity: item.quantity,
          cartons: item.cartons,
          cups: item.cups
        })),
        pricing: {
          netTotal: netTotal,
          shipping: shippingCost,
          vatRate: VAT_RATE,
          vatAmount: vatAmount,
          finalTotal: finalTotal
        }
      };

      const response = await fetch(`${backendUrl}/api/orders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      const result = await response.json();

      if (response.ok && result.payment_url) {
        // Redirect to Mollie payment page
        window.location.href = result.payment_url;
      } else if (response.ok && result.order_id) {
        // If no payment URL but order created (Mollie not configured)
        alert(`Order created successfully! Order ID: ${result.order_id}\n\nNote: Payment gateway integration pending. Please contact us to complete your order.`);
        clearCart();
        navigate('/');
      } else {
        throw new Error(result.detail || 'Failed to create order');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert(`Error: ${error.message || 'Failed to process order. Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  // If cart is empty, show message
  if (cartItems.length === 0) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="empty-checkout">
            <ShoppingBag size={80} strokeWidth={1} />
            <h2>Your cart is empty</h2>
            <p>Add some delicious edible cups to get started!</p>
            <Link to="/" className="btn-primary">
              Browse Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      {/* Breadcrumb */}
      <nav className="breadcrumb-nav">
        <div className="container">
          <ol className="breadcrumb">
            <li><Link to="/">Home</Link></li>
            <ChevronRight size={14} />
            <li className="current">Checkout</li>
          </ol>
        </div>
      </nav>

      <div className="checkout-container">
        <h1 className="checkout-title">
          <ShoppingBag size={32} />
          Checkout
        </h1>

        <div className="checkout-layout">
          {/* Left Column - Customer Form */}
          <div className="checkout-form-section">
            <form onSubmit={handleSubmit} className="checkout-form">
              {/* Customer Information */}
              <div className="form-section">
                <h2 className="form-section-title">
                  <User size={20} />
                  Customer Information
                </h2>

                {/* Customer Type */}
                <div className="customer-type-selector">
                  <label 
                    className={`type-option ${formData.customerType === 'private' ? 'active' : ''}`}
                  >
                    <input
                      type="radio"
                      name="customerType"
                      value="private"
                      checked={formData.customerType === 'private'}
                      onChange={handleChange}
                    />
                    <User size={18} />
                    <span>Private</span>
                  </label>
                  <label 
                    className={`type-option ${formData.customerType === 'business' ? 'active' : ''}`}
                  >
                    <input
                      type="radio"
                      name="customerType"
                      value="business"
                      checked={formData.customerType === 'business'}
                      onChange={handleChange}
                    />
                    <Building2 size={18} />
                    <span>Business</span>
                  </label>
                </div>

                {/* Company Name - Only shown for Business customers */}
                {formData.customerType === 'business' && (
                  <div className="form-group company-name-field">
                    <label htmlFor="companyName">Company Name *</label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      className={errors.companyName ? 'error' : ''}
                      placeholder="Enter your company name"
                    />
                    {errors.companyName && <span className="error-message"><AlertCircle size={14} /> {errors.companyName}</span>}
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={errors.firstName ? 'error' : ''}
                      placeholder="Enter your first name"
                    />
                    {errors.firstName && <span className="error-message"><AlertCircle size={14} /> {errors.firstName}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={errors.lastName ? 'error' : ''}
                      placeholder="Enter your last name"
                    />
                    {errors.lastName && <span className="error-message"><AlertCircle size={14} /> {errors.lastName}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? 'error' : ''}
                      placeholder="your@email.com"
                    />
                    {errors.email && <span className="error-message"><AlertCircle size={14} /> {errors.email}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="mobile">Mobile *</label>
                    <input
                      type="tel"
                      id="mobile"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      className={errors.mobile ? 'error' : ''}
                      placeholder="+31 6 12345678"
                    />
                    {errors.mobile && <span className="error-message"><AlertCircle size={14} /> {errors.mobile}</span>}
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="form-section">
                <h2 className="form-section-title">
                  <Truck size={20} />
                  Shipping Address
                </h2>

                <div className="form-row">
                  <div className="form-group flex-2">
                    <label htmlFor="streetName">Street Name *</label>
                    <input
                      type="text"
                      id="streetName"
                      name="streetName"
                      value={formData.streetName}
                      onChange={handleChange}
                      className={errors.streetName ? 'error' : ''}
                      placeholder="Enter street name"
                    />
                    {errors.streetName && <span className="error-message"><AlertCircle size={14} /> {errors.streetName}</span>}
                  </div>
                  <div className="form-group flex-1">
                    <label htmlFor="houseNumber">House No. *</label>
                    <input
                      type="text"
                      id="houseNumber"
                      name="houseNumber"
                      value={formData.houseNumber}
                      onChange={handleChange}
                      className={errors.houseNumber ? 'error' : ''}
                      placeholder="123"
                    />
                    {errors.houseNumber && <span className="error-message"><AlertCircle size={14} /> {errors.houseNumber}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="zipCode">Zip/Postal Code *</label>
                    <input
                      type="text"
                      id="zipCode"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                      className={errors.zipCode ? 'error' : ''}
                      placeholder="1234 AB"
                    />
                    {errors.zipCode && <span className="error-message"><AlertCircle size={14} /> {errors.zipCode}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={errors.city ? 'error' : ''}
                      placeholder="Amsterdam"
                    />
                    {errors.city && <span className="error-message"><AlertCircle size={14} /> {errors.city}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="country">Country *</label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={errors.country ? 'error' : ''}
                  >
                    <option value="Netherlands">Netherlands</option>
                    <option value="Belgium">Belgium</option>
                  </select>
                  {errors.country && <span className="error-message"><AlertCircle size={14} /> {errors.country}</span>}
                </div>
              </div>

              {/* Create Account Section */}
              <div className="create-account-section">
                <label className="create-account-checkbox">
                  <input
                    type="checkbox"
                    checked={createAccount}
                    onChange={(e) => {
                      setCreateAccount(e.target.checked);
                      if (!e.target.checked) {
                        setPassword('');
                        setConfirmPassword('');
                        setErrors(prev => ({ ...prev, password: '', confirmPassword: '' }));
                      }
                    }}
                  />
                  <span>Create an account for faster checkout next time</span>
                </label>
                
                {createAccount && (
                  <div className="account-password-fields">
                    <p className="account-info-text">
                      <CheckCircle size={16} />
                      Your account will be created using your email: <strong>{formData.email || 'Enter email above'}</strong>
                    </p>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <input
                          type="password"
                          id="password"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
                          }}
                          className={errors.password ? 'error' : ''}
                          placeholder="Min. 6 characters"
                        />
                        {errors.password && <span className="error-message"><AlertCircle size={14} /> {errors.password}</span>}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password *</label>
                        <input
                          type="password"
                          id="confirmPassword"
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: '' }));
                          }}
                          className={errors.confirmPassword ? 'error' : ''}
                          placeholder="Confirm your password"
                        />
                        {errors.confirmPassword && <span className="error-message"><AlertCircle size={14} /> {errors.confirmPassword}</span>}
                      </div>
                    </div>
                  </div>
                )}
                
                {accountCreationStatus === 'success' && (
                  <div className="account-status success">
                    <CheckCircle size={16} />
                    Account created successfully! You can log in after checkout.
                  </div>
                )}
                
                {accountCreationStatus === 'error' && (
                  <div className="account-status error">
                    <AlertCircle size={16} />
                    Could not create account (email may already exist). Your order will continue as guest.
                  </div>
                )}
              </div>

              {/* Terms & Conditions */}
              <div className="terms-section">
                <label className="terms-checkbox">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => {
                      setAcceptedTerms(e.target.checked);
                      if (errors.terms) setErrors(prev => ({ ...prev, terms: '' }));
                    }}
                  />
                  <span>
                    I agree to the{' '}
                    <Link to="/privacy-policy" target="_blank">Privacy Policy</Link>
                    {' '}and{' '}
                    <Link to="/terms-conditions" target="_blank">Terms & Conditions</Link>
                  </span>
                </label>
                {errors.terms && <span className="error-message"><AlertCircle size={14} /> {errors.terms}</span>}
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                className="checkout-submit-btn"
                disabled={loading}
              >
                <CreditCard size={20} />
                {loading ? 'Processing...' : 'Complete Order with iDEAL'}
              </button>

              <p className="payment-note">
                <img src="https://www.mollie.com/external/icons/payment-methods/ideal.svg" alt="iDEAL" className="ideal-icon" />
                You will be redirected to iDEAL to complete your payment securely.
              </p>
            </form>
          </div>

          {/* Right Column - Order Summary */}
          <div className="checkout-summary-section">
            <div className="order-summary-card">
              <h2 className="summary-title">Order Summary</h2>

              {/* Cart Items */}
              <div className="summary-items">
                {cartItems.map((item) => {
                  const itemDetails = getItemDetails(item);
                  const itemPrice = getItemPrice(item);
                  const priceInfo = getItemPriceInfo ? getItemPriceInfo(item) : { hasDiscount: false, originalPrice: itemPrice, discountedPrice: itemPrice };
                  const isTieredProduct = item.id === 'edible-cup-110' || item.id === 'edible-cup-220';
                  
                  return (
                    <div key={item.cartItemId} className="summary-item">
                      <div className="summary-item-image">
                        <img src={item.image} alt={item.name} />
                      </div>
                      <div className="summary-item-details">
                        <h4>{item.name}</h4>
                        <p className="summary-item-variant">{itemDetails.variant}</p>
                        <div className="summary-item-controls">
                          {/* Tiered products (cups) - show carton controls */}
                          {isTieredProduct ? (
                            <div className="quantity-controls small">
                              <button 
                                type="button"
                                onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                                disabled={!canDecreaseCartons(item.cartItemId)}
                                title="Previous pricing tier"
                              >
                                <Minus size={12} />
                              </button>
                              <span>{item.cartons}</span>
                              <button 
                                type="button"
                                onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                                disabled={!canIncreaseCartons(item.cartItemId)}
                                title="Next pricing tier"
                              >
                                <Plus size={12} />
                              </button>
                            </div>
                          ) : isCutleryProduct(item.id) ? (
                            // Cutlery products - show pack quantity controls with total units
                            <div className="cutlery-quantity-wrapper">
                              <div className="quantity-controls small">
                                <button 
                                  type="button"
                                  onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                                  disabled={item.quantity <= 1}
                                  title="Decrease quantity"
                                >
                                  <Minus size={12} />
                                </button>
                                <span>{item.quantity}</span>
                                <button 
                                  type="button"
                                  onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                                  title="Increase quantity"
                                >
                                  <Plus size={12} />
                                </button>
                              </div>
                              <div className="total-units-display">
                                <span className="total-units">= {calculateTotalUnits(item.id, item.quantity)} units</span>
                              </div>
                            </div>
                          ) : (
                            // Other products - standard quantity controls
                            <div className="quantity-controls small">
                              <button 
                                type="button"
                                onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                              >
                                <Minus size={12} />
                              </button>
                              <span>{item.quantity}</span>
                              <button 
                                type="button"
                                onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                              >
                                <Plus size={12} />
                              </button>
                            </div>
                          )}
                          <button 
                            type="button"
                            className="remove-btn"
                            onClick={() => removeFromCart(item.cartItemId)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        {isTieredProduct && (
                          <p className="tier-info">Cartons: {item.cartons}</p>
                        )}
                        {isCutleryProduct(item.id) && (
                          <p className="tier-info">Total: {calculateTotalUnits(item.id, item.quantity)} {item.name.toLowerCase().includes('spoon') ? 'spoons' : item.name.toLowerCase().includes('fork') ? 'forks' : 'stirrers'}</p>
                        )}
                      </div>
                      <div className={`summary-item-price ${priceInfo.hasDiscount ? 'has-discount' : ''}`}>
                        {priceInfo.hasDiscount && (
                          <span className="original-price">€{priceInfo.originalPrice.toFixed(2)}</span>
                        )}
                        <span className={priceInfo.hasDiscount ? 'discounted-price' : ''}>
                          €{itemPrice.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Price Breakdown */}
              <div className="price-breakdown">
                {/* Discount savings banner */}
                {discountActive && cartSavings > 0 && (
                  <div className="discount-savings-banner">
                    <span>🎉 {getDiscountPercentage()} OFF Applied!</span>
                    <span className="savings-amount">You save €{cartSavings.toFixed(2)}</span>
                  </div>
                )}
                
                <div className={`price-row ${discountActive ? 'has-discount' : ''}`}>
                  <span>Total Net</span>
                  <div className="price-value">
                    {discountActive && (
                      <span className="original-price">€{originalNetTotal.toFixed(2)}</span>
                    )}
                    <span className={discountActive ? 'discounted-price' : ''}>€{netTotal.toFixed(2)}</span>
                  </div>
                </div>
                <div className={`price-row ${isFreeShipping ? 'free-shipping' : ''}`}>
                  <span>
                    <Truck size={16} />
                    Shipping
                    <small>(Insured shipping & home delivery)</small>
                  </span>
                  {isFreeShipping ? (
                    <span className="free-shipping-text">
                      <span className="original-price">€{SHIPPING_COST_BASE.toFixed(2)}</span>
                      FREE
                    </span>
                  ) : (
                    <span>€{shippingCost.toFixed(2)}</span>
                  )}
                </div>
                <div className="free-shipping-notice">
                  <small>Free shipping on orders over 50 €.</small>
                </div>
                <div className="price-row">
                  <span>VAT (9%)</span>
                  <span>€{vatAmount.toFixed(2)}</span>
                </div>
                <div className="price-row total">
                  <span>Final Total</span>
                  <span>€{finalTotal.toFixed(2)}</span>
                </div>
              </div>

              {/* Secure Checkout Badge */}
              <div className="secure-badge">
                <CheckCircle size={18} />
                <span>Secure checkout powered by Mollie</span>
              </div>
            </div>

            {/* Continue Shopping Link */}
            <Link to="/" className="continue-shopping-link">
              ← Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
